import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { Button } from '../../../../components/common'
import { ROLE_DISPLAY_NAME } from '../../../Login/api'
import { useTranningCalender } from './api'

interface ProgramShedulesProps {
  filters?: Record<string, any>
  displayStatusAsIs?: boolean
}

type TrainingStatus =
  | 'Upcoming'
  | 'Ongoing'
  | 'Closed'
  | 'Completed'
  | 'Current'

export default function ProgramShedules({
  filters = {},
  displayStatusAsIs = false,
}: ProgramShedulesProps) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useTranningCalender(filters)
  const trainings = data?.data || []
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  const statusOrder: Record<TrainingStatus, number> = {
    Upcoming: 1,
    Ongoing: 2,
    Current: 2,
    Closed: 3,
    Completed: 3,
  }

  const getEventStatus = (eventDate: string): TrainingStatus => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const date = new Date(eventDate)
    date.setHours(0, 0, 0, 0)
    if (date < today) return 'Completed'
    if (date.getTime() === today.getTime()) return 'Current'
    return 'Upcoming'
  }

  const isBookingOpen = (bookingEndDate: string) => {
    if (!bookingEndDate) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const endDate = new Date(bookingEndDate)
    endDate.setHours(0, 0, 0, 0)

    return endDate >= today
  }
  const sortedTrainings = displayStatusAsIs
    ? [...trainings].sort((a, b) => {
        const statusA = (a.status || 'Upcoming') as TrainingStatus
        const statusB = (b.status || 'Upcoming') as TrainingStatus
        return (statusOrder[statusA] || 4) - (statusOrder[statusB] || 4)
      })
    : [...trainings].sort((a, b) => {
        const statusA = getEventStatus(a.date)
        const statusB = getEventStatus(b.date)
        return statusOrder[statusA] - statusOrder[statusB]
      })

  const statusStyles: Record<TrainingStatus, string> = {
    Upcoming: 'bg-[#E44342] text-white',
    Ongoing: 'bg-linear-orange text-white',
    Closed: 'bg-gray-400 text-white',
    Completed: 'bg-gray-400 text-white',
    Current: 'bg-linear-orange text-white',
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  if (isLoading)
    return <div className="text-center py-10">Loading programs...</div>

  if (isError)
    return (
      <div className="text-center py-10 text-red-500">
        Failed to load trainings
      </div>
    )

  if (sortedTrainings.length === 0)
    return (
      <div className="text-center text-gray-500 py-10">No programs found</div>
    )

  return (
    <div className="py-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
      {sortedTrainings.map((event: any, index: number) => {
        const apiStatus = (event.status || 'Upcoming') as TrainingStatus
        const status = apiStatus === 'Ongoing' ? 'Current' : apiStatus
        // const isUpcoming = status === 'Upcoming'

        return (
          <div
            key={event.id || index}
            className="bg-white shadow-md rounded-2xl overflow-hidden flex flex-col border hover:shadow-lg transition-all duration-300"
          >
            {/* Image section */}
            <div className="relative">
              <img
                src={event.image}
                alt={event.title}
                className="w-full h-64 object-cover"
              />
              <button
                onClick={() => setPreviewImage(event.image)}
                className="absolute top-3 right-3 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition"
              >
                <Eye className="w-5 h-5" />
              </button>
            </div>

            {/* Card content */}
            <div className="p-5 flex flex-col justify-between flex-1">
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span
                    className={`px-4 py-1 rounded-full text-xs font-semibold ${
                      statusStyles[status] || statusStyles.Upcoming
                    }`}
                  >
                    {status}
                  </span>
                  <span
                    className={`font-medium text-sm ${
                      event.mode?.includes('Online')
                        ? 'text-orange-500'
                        : 'text-green-600'
                    }`}
                  >
                    {event.mode}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {event.name}
                </h3>

                <p className="text-sm text-blue-500 mb-4">
                  Last Date Of Registration:{' '}
                  <span className="font-medium">
                    {formatDate(event.booking_end_date)}
                  </span>
                </p>

                <h4 className="text-sm font-semibold text-gray-700 mb-1">
                  Eligible Applicants:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(event.role_id || []).map((roleId: number) => {
                    const roleName = ROLE_DISPLAY_NAME[roleId]
                    return (
                      <span
                        key={roleId}
                        className="text-xs bg-[#f5eded] text-gray-800 rounded-full px-3 py-1"
                      >
                        {roleName}
                      </span>
                    )
                  })}
                </div>
              </div>

              {/* Highlighted Start and End Dates */}
              <div className="flex flex-col justify-between mt-5 border-t pt-3">
                <div className="text-sm space-y-2">
                  <p className="flex justify-start items-center space-x-1">
                    <span className="font-semibold text-gray-800">Start:</span>{' '}
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-md font-semibold">
                      {formatDate(event.date)}
                    </span>
                  </p>
                  <p className="flex justify-start items-center space-x-3">
                    <span className="font-semibold text-gray-800">End:</span>{' '}
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-md font-semibold">
                      {formatDate(event.end_date)}
                    </span>
                  </p>
                </div>
                <div className="flex justify-center mt-3">
                  {isBookingOpen(event.booking_end_date) ? (
                    <Button
                      onClick={() =>
                        navigate(
                          `/departments/learning/program-details/${event.id}`
                        )
                      }
                      label="Apply Now"
                      outlined
                      className="border border-LightGreen text-LightGreen px-4 py-2 hover:bg-LightGreen hover:text-white text-sm"
                    />
                  ) : (
                    <Button
                      label="Enrollment Closed"
                      outlined
                      disabled
                      className="border border-gray-400 text-gray-400 px-4 py-2 text-sm"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {/* Enlarged Image Modal with Solid Black Background */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black flex items-center justify-center z-50"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative w-fit max-w-[90vw] rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewImage}
              alt="Preview"
              className="block h-[90vh] w-auto max-w-full object-contain rounded-xl shadow-lg"
            />
            <button
              className="absolute top-3 right-3 text-white bg-black/70 rounded-full p-2 hover:bg-black transition"
              onClick={() => setPreviewImage(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
